import { useEffect, useMemo, useRef, useState } from "react";

// Redux
import { useSelector } from "react-redux";

// MUI
import { Grid, Stack } from "@mui/material";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// API
import { onCreateOEMChevroletCsi, onCreateOEMMitsubishiCsi, onCreateOEMVolvoCsi } from "@DataPages/api/myOEM";

// Components
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import CollapsibleLeftPanel from "@Layouts/components/CollapsibleLeftPanel";
import CommissionSideBar from "@Layouts/components/SideBars/CommissionSideBar";
import TextIconMessage from "@Layouts/components/Basics/TextIconMessage";
import MyOEMDataBody from "./MyOEMDataBody";

const QUERY_IDENTIFIERS = ["myOemData"];

export default function MyOEMDataLayer() {
	const { clearCache } = useCacheCleaner();
	const lastSelected = useRef(null);
	const open = useRef(true);
	const location = useSelector((state) => state.location.location);
	const [selection, setSelection] = useState(null);
	const [updateData, setUpdateData] = useState(null);
	const [loading, setLoading] = useState(true);
	const { addSubscription, clearSubscriptionContext } = useSubscriptions();

	const sideBar = useMemo(() => {
		const sideSelection = function (selected, isActualCycle) {
			if (selected?._id !== lastSelected?._id) {
				open.current = !open.current;
				lastSelected.current = selected;
				setSelection(selected);
				setLoading(false);

				if (isActualCycle) {
					addSubscription(
						["myOemData", selected._id],
						[
							{
								context: "onCreateOEMChevroletCsi",
								query: onCreateOEMChevroletCsi,
								variables: {
									commissionCycleId: selected._id,
								},
							},
							{
								context: "onCreateOEMMitsubishiCsi",
								query: onCreateOEMMitsubishiCsi,
								variables: {
									commissionCycleId: selected._id,
								},
							},
							{
								context: "onCreateOEMVolvoCsi",
								query: onCreateOEMVolvoCsi,
								variables: {
									commissionCycleId: selected._id,
								},
							},
						]
					);
				}
			}
		};

		return <CommissionSideBar callbackSelected={sideSelection} selectDefaultLastCommissionCycle />;
	}, [addSubscription]);

	useEffect(() => {
		return () => {
			setSelection(null);
			setLoading(true);
			lastSelected.current = null;
			open.current = true;
			clearCache(QUERY_IDENTIFIERS);
			clearSubscriptionContext();
		};
	}, [clearSubscriptionContext, location]);

	return (
		<>
			<BodyLayer floatingContent={<CollapsibleLeftPanel permanentTrigger={open.current} control={sideBar} />}>
				<RoundedBox sx={{ minHeight: "630px" }}>
					<MyOEMDataBody
						selected={selection}
						isUpdatable={Boolean(selection?.status === "CLOSING" || selection?.status === "ACTIVE")}
						loading={loading}
						updateData={(data) => {
							setUpdateData(data);
						}}
					/>
				</RoundedBox>
			</BodyLayer>
			<Grid item xs={12} mb={2} paddingLeft="20px">
				<Stack direction={"row"} alignItems={"center"}>
					<TextIconMessage icon title={"Last Update"} content={updateData} showContent={Boolean(updateData)} />
				</Stack>
			</Grid>
		</>
	);
}
